"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { observer } from "mobx-react-lite";
import { Search, ChevronDown, Plus, Minus, X as XIcon } from "lucide-react";
import DateRangePicker from "./DateRangePicker";
import searchStore from "../store/searchStore";
import { logoLinks } from "./logoLinks";

const HotelHeaderSearchField = observer(function HotelHeaderSearchField() {
  const [showAgeDropdown, setShowAgeDropdown] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const guestsRef = useRef(null);
  const ageDropdownRef = useRef(null);

  // Значения из searchStore (реактивно через observer)
  const checkIn = searchStore.dateCheckin;
  const checkOut = searchStore.dateCheckout;
  const adults = searchStore.guestsCount;
  const childrenAges = searchStore.guestsChildren;
  const withPet = searchStore.dogFriendly;

  const handleAdultsChange = (newValue) => {
    searchStore.setGuestsCount(newValue);
  };

  const handleAddChild = (age) => {
    searchStore.addGuestsChildren(age);
    setShowAgeDropdown(false);
  };

  const handleRemoveChild = (index) => {
    searchStore.delGuestsChildren(index);
  };

  const handlePetToggle = () => {
    searchStore.setDogFriendly(!withPet);
  };

  const handleSearch = () => {
    window.location.reload();
  };

  // Внешний клик — закрываем дропдауны
  useEffect(() => {
    function handleOutside(e) {
      if (guestsRef.current && !guestsRef.current.contains(e.target)) {
        setGuestsOpen(false);
      }
      if (
        ageDropdownRef.current &&
        !ageDropdownRef.current.contains(e.target)
      ) {
        setShowAgeDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const guestsLabel = [
    `${adults} ${adults === 1 ? "гость" : adults < 5 ? "гостя" : "гостей"}`,
    childrenAges.length
      ? `${childrenAges.length} ${childrenAges.length === 1 ? "ребёнок" : "детей"}`
      : null,
    withPet ? "🐕" : null,
  ]
    .filter(Boolean)
    .join(", ");

  // ============ Логотип с меню ============
  const LogoDropdownInline = () => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
      function handleOutside(e) {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpen(false);
        }
      }
      document.addEventListener("mousedown", handleOutside);
      return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const breadcrumbs = {
      "": [{ label: "Гостим", href: "/" }],
      Sprav: [
        { label: "Гостим", href: "/" },
        { label: "Поиск отелей", href: "/russia" },
      ],
    };

    const items = breadcrumbs[""];

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0"
        >
          <img
            src="/favicon-120x120.png"
            alt="Гостим"
            className="h-8 sm:h-9 w-auto object-contain"
          />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64">
            <div className="pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                {items.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <a
                      href={item.href}
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {item.label}
                    </a>
                    {idx < items.length - 1 && (
                      <span className="text-gray-300">/</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="pt-3 space-y-2">
              {logoLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5 text-gray-400" />
                    {link.label}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============ Дропдаун гостей ============
  const GuestsDropdown = ({ anchor }) => (
    <div
      className={`bg-white border border-gray-200 rounded-xl shadow-xl p-4 z-[9999] space-y-3 ${
        anchor === "mobile"
          ? "fixed left-1/2 transform -translate-x-1/2 top-20 w-[calc(100vw-2rem)] max-w-md"
          : "absolute top-full mt-2 right-0 w-72"
      }`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Взрослые</div>
          <div className="text-xs text-gray-400">старше 18 лет</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAdultsChange(Math.max(1, adults - 1));
            }}
            className="w-7 h-7 rounded-full border border-gray-600 text-gray-700 flex items-center justify-center hover:bg-gray-700 hover:text-white hover:border-gray-700 transition-colors"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="text-sm font-semibold w-6 text-center text-gray-900">
            {adults}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAdultsChange(adults + 1);
            }}
            className="w-7 h-7 rounded-full border border-gray-600 text-gray-700 flex items-center justify-center hover:bg-gray-700 hover:text-white hover:border-gray-700 transition-colors"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative" ref={ageDropdownRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAgeDropdown((v) => !v);
            }}
            className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Ребёнок
          </button>
          {showAgeDropdown && (
            <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-36 max-h-52 overflow-y-auto">
              {Array.from({ length: 17 }, (_, i) => i + 1).map((age) => (
                <button
                  key={age}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddChild(age);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {age} {age === 1 ? "год" : age < 5 ? "года" : "лет"}
                </button>
              ))}
            </div>
          )}
        </div>
        {childrenAges.map((age, idx) => (
          <span
            key={idx}
            className="flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-sm text-gray-700"
          >
            {age} {age === 1 ? "год" : age < 5 ? "года" : "лет"}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveChild(idx);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePetToggle();
        }}
        className={`text-sm transition-colors ${withPet ? "text-gray-900 font-medium" : "text-gray-500"}`}
      >
        {withPet ? "Едем с собакой 🐕" : "Едем без собаки"}
      </button>
    </div>
  );

  return (
    <>
      {/* Мобильная версия: даты + гости в одной строке */}
      <div className="sm:hidden">
        <div
          className="flex items-center bg-white rounded-full shadow-lg gap-0 h-12"
          style={{ paddingLeft: "6px", paddingRight: "6px" }}
        >
          <div
            className="flex items-center flex-shrink-0 pl-1.5 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <LogoDropdownInline />
          </div>
          <div className="flex items-center flex-1 min-w-0 gap-1">
            <div className="relative z-[9999]">
              <DateRangePicker
                checkIn={checkIn}
                checkOut={checkOut}
                onChange={({ checkIn: ci, checkOut: co }) => {
                  searchStore.setDateCheckin(ci);
                  searchStore.setDateCheckout(co);
                }}
                accentColor="#14b8a6"
                triggerVariant="pill"
                closeSignal={guestsOpen || undefined}
                onOpen={() => setGuestsOpen(false)}
              />
            </div>
            <div className="w-px h-5 bg-gray-200 flex-shrink-0" />
            <div className="relative flex-shrink-0" ref={guestsRef}>
              <button
                onClick={() => setGuestsOpen((v) => !v)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors text-gray-700 hover:bg-gray-100"
              >
                <span className="text-xs whitespace-nowrap">{guestsLabel}</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>
            </div>
          </div>
          <button onClick={handleSearch} className="flex-shrink-0">
            <span
              className="flex items-center justify-center w-9 h-9 rounded-full text-white cursor-pointer"
              style={{
                background: "linear-gradient(to right, #14b8a6, #3b82f6)",
              }}
            >
              <Search className="w-4 h-4" />
            </span>
          </button>
        </div>
      </div>

      {/* Desktop версия */}
      <div
        className="hidden sm:flex items-center bg-white rounded-full shadow-xl gap-0 h-12"
        style={{ paddingLeft: "6px", paddingRight: "6px" }}
      >
        <div className="flex items-center flex-shrink-0">
          <LogoDropdownInline />
        </div>
        <div className="flex items-center flex-1 min-w-0 gap-1 px-2">
          <div className="relative z-[9999]">
            <DateRangePicker
              checkIn={checkIn}
              checkOut={checkOut}
              onChange={({ checkIn: ci, checkOut: co }) => {
                searchStore.setDateCheckin(ci);
                searchStore.setDateCheckout(co);
              }}
              accentColor="#14b8a6"
              triggerVariant="pill"
              closeSignal={guestsOpen || undefined}
              onOpen={() => setGuestsOpen(false)}
            />
          </div>
          <div className="w-px h-5 bg-gray-200 flex-shrink-0" />
          <div className="relative flex-shrink-0" ref={guestsRef}>
            <button
              onClick={() => setGuestsOpen((v) => !v)}
              className="flex items-center gap-1 px-3 py-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <span className="text-sm text-gray-700 whitespace-nowrap">
                {guestsLabel}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            </button>
            {guestsOpen && <GuestsDropdown anchor="desktop" />}
          </div>
        </div>
        <button onClick={handleSearch} className="flex-shrink-0">
          <span
            className="flex items-center justify-center w-9 h-9 rounded-full text-white cursor-pointer"
            style={{
              background: "linear-gradient(to right, #14b8a6, #3b82f6)",
            }}
          >
            <Search className="w-4 h-4" />
          </span>
        </button>
      </div>

      {/* Мобильный модал гостей (накладывается поверх) */}
      <AnimatePresence>
        {guestsOpen &&
          typeof window !== "undefined" &&
          window.innerWidth < 640 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10001] sm:hidden"
            >
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setGuestsOpen(false)}
              />
              <div className="absolute left-4 right-4 top-20 mx-auto max-w-md">
                <GuestsDropdown anchor="mobile" />
              </div>
            </motion.div>
          )}
      </AnimatePresence>
    </>
  );
});

export default HotelHeaderSearchField;
